import type { BotRef, BotSystem } from './botNav.ts';

/**
 * A2 step 1 (chatbot_v2_mono docs/superpowers/specs/2026-10-06-ai-console-v2-first-design.md §A2): the bot's
 * instructions (tool-service static context, fronted by the hub) and its readiness — what decides whether a bot
 * answers at all. Pure helpers; the components only render.
 */

/** Same ceiling as the hub backend (`MAX_INSTRUCTIONS_CHARS`). */
export const INSTRUCTIONS_MAX_CHARS = 20_000;

const enc = encodeURIComponent;

export const instructionsPath = (bot: BotRef): string => `/api/bot-instructions/${bot.system}/${enc(bot.botId)}`;
export const readinessPath = (bot: BotRef): string => `/api/bot-readiness/${bot.system}/${enc(bot.botId)}`;

/** Why the text cannot be saved, or null. Removing is a separate action, so blank is refused. */
export function validateInstructions(text: string): string | null {
  const t = text.trim();
  if (!t) return 'Instructions are empty — write them, or use Remove.';
  if (t.length > INSTRUCTIONS_MAX_CHARS) return `Instructions are limited to ${INSTRUCTIONS_MAX_CHARS} characters (now ${t.length}).`;
  return null;
}

/** Section ids on the AI tab; "Fix" on a failing readiness check scrolls there (keys from BotReadinessService). */
export type AiSection = 'ai-instructions' | 'ai-active' | 'ai-routing' | 'ai-model' | 'ai-embedding';

export function fixSection(checkKey: string): AiSection | null {
  switch (checkKey) {
    case 'instructions':
      return 'ai-instructions';
    case 'active':
    case 'runtimeConfig': // saving the active toggle once creates the hub row
      return 'ai-active';
    case 'llmModel':
      return 'ai-model';
    case 'embeddingProfile':
      return 'ai-embedding';
    case 'routing':
      return 'ai-routing';
    default:
      return null;
  }
}

const CHECK_LABELS: Record<string, string> = {
  runtimeConfig: 'Base config',
  active: 'Active',
  embeddingProfile: 'Embedding profile',
  instructions: 'Instructions',
  llmModel: 'LLM model',
  hub: 'AI service',
  routing: 'Channels (LLM on/off)',
};

/** Operator wording for a readiness check (as v1 /hub/botconfig); unknown keys are shown as-is. */
export const checkLabel = (key: string): string => CHECK_LABELS[key] ?? key;

/**
 * Flow v2 (tool use) is still being built in tool-service (owner decision 2026-10-08): only global admins and
 * services switch it. Not `showPlatform()` — under HUB_AUTHZ_MODE=shadow that is true for every operator.
 */
export function canSwitchFlow(me: { kind: string; isGlobalAdmin?: boolean } | null): boolean {
  if (!me) return false;
  return me.kind === 'service' || me.isGlobalAdmin === true;
}

export function flowLabel(flowVersion: string | undefined | null): string {
  return flowVersion === 'v2' ? 'v2 — experimental (tool use, in development)' : 'v1 (current)';
}

/**
 * The hub's active switch. Without a hub row tool-service follows the legacy Qdrant payload flag (or answers
 * "not configured" when there is nothing there), so `unset` is its own state — never rendered as a ticked box.
 */
export function activeStatus(config: { isActive: boolean } | null): 'on' | 'off' | 'unset' {
  if (!config) return 'unset';
  return config.isActive ? 'on' : 'off';
}

export type Tone = 'ok' | 'bad' | 'unknown';

/** `unknown` (a probe could not run) is never rendered as fine or as broken. */
export function checkTone(status: string): Tone {
  if (status === 'ok') return 'ok';
  if (status === 'failed') return 'bad';
  return 'unknown';
}

export function rollupText(ready: string): string {
  if (ready === 'ready') return 'Configured to answer with the LLM';
  if (ready === 'not_ready') return 'Not ready — the LLM will not answer until the failing checks are fixed';
  return 'Could not check everything — some probes did not run';
}

/** hubClient errors read `"<status> <text> — <body>"`; show the server's `message` when the body has one. */
export function errorText(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const i = msg.indexOf(' — ');
  // hubClient: "<status> <text> — <body>"; hubService: the raw body.
  const raw = i >= 0 ? msg.slice(i + 3) : msg;
  if (!raw.trimStart().startsWith('{')) return msg;
  try {
    const body = JSON.parse(raw) as { message?: unknown };
    if (typeof body.message === 'string' && body.message) return body.message;
    if (Array.isArray(body.message) && body.message.length) return body.message.join('; ');
  } catch {
    /* not JSON — fall through */
  }
  return msg;
}

/**
 * The bot routing record's fields per system (bot routing R1, spec chatbot_v2_mono 2026-10-09-bot-routing-design.md),
 * in display order with operator labels. Mirrors the hub's `bot-routing.fields.ts` — the server validates every
 * change; this only lays the section out.
 */
const ROUTING_UI: Record<BotSystem, { field: string; label: string; options: string[]; def: string }[]> = {
  chatbot: [
    { field: 'dm_llm', label: 'Messenger DM LLM', options: ['on', 'off'], def: 'off' },
    { field: 'comment_llm', label: 'Comment LLM', options: ['on', 'off'], def: 'off' },
    { field: 'dm_intents', label: 'DM intents engine', options: ['rasa', 'hub', 'none'], def: 'rasa' },
    { field: 'comment_intents', label: 'Comment intents engine', options: ['rasa', 'hub'], def: 'rasa' },
    { field: 'no_knowledge_reply', label: 'When the AI finds nothing', options: ['ai_text', 'default_block'], def: 'ai_text' },
    { field: 'block_choice', label: 'Which block goes with an answer', options: ['ai', 'score', 'shadow'], def: 'ai' },
    { field: 'block_links', label: 'AI sends linked blocks', options: ['off', 'shadow', 'on'], def: 'off' },
  ],
  chatbot_v2: [
    { field: 'instagram_llm', label: 'Instagram LLM', options: ['on', 'off'], def: 'off' },
    { field: 'messenger_llm', label: 'Messenger LLM', options: ['on', 'off'], def: 'off' },
    { field: 'no_knowledge_reply', label: 'When the AI finds nothing', options: ['ai_text', 'default_block'], def: 'ai_text' },
    { field: 'block_links', label: 'AI sends linked blocks', options: ['off', 'shadow', 'on'], def: 'off' },
  ],
};

export interface RoutingRow {
  field: string;
  label: string;
  value: string;
  options: string[];
}

export function routingRows(system: BotSystem, flow: Record<string, string>): RoutingRow[] {
  return ROUTING_UI[system].map(({ field, label, options, def }) => ({
    field,
    label,
    options,
    value: options.includes(flow?.[field]) ? flow[field] : def,
  }));
}

